/**
 * A conversa do cliente com o servidor, por cima de um `Transport` qualquer.
 *
 * Só isto sabe a ORDEM do protocolo: `hello` primeiro, `welcome` antes de qualquer
 * outra coisa, e só então `input`/`snapshot`. O transporte abaixo só move bytes;
 * o render acima NEVER vê byte nenhum.
 *
 * Invariante 2: daqui só sai INTENÇÃO. NEVER mande posição, acerto, moeda ou
 * resultado — quem decide isso é a sala.
 */
import {
  DecodeError,
  decode,
  encode,
  MIN_SUPPORTED_VERSION,
  PROTOCOL_VERSION,
  type AnyMessage,
  type MessageData,
  type Transport,
} from '@game/protocol';
import { WebSocketTransport } from './websocket-transport.js';

export type Welcome = MessageData<'welcome'>;
export type Snapshot = MessageData<'snapshot'>;

/** O que o jogador quis fazer neste tick. NEVER onde ele acha que está. */
export interface Intent {
  /** Tick que o cliente acredita estar simulando. O servidor valida. */
  tick: number;
  moveX: number;
  moveZ: number;
  /** Bitmask de ações. */
  actions: number;
}

/** O servidor recusou a entrada. `code` é `REJECT_CODES` de `@game/protocol`. */
export class RejectedError extends Error {
  constructor(
    readonly code: number,
    message: string,
  ) {
    super(message);
  }
}

export interface ConnectOptions {
  url: string;
  roomId: string;
  guestToken: string;
  /** Injetável: transporte falso com atraso e perda entra aqui na fase 01 (REV-05). */
  transport?: Transport;
  /** Relógio monotônico. `performance.now` por padrão; teste injeta. */
  now?: () => number;
  /**
   * Teto entre o `hello` e o `welcome`. Um servidor que aceita o socket e emudece
   * é indistinguível de rede lenta sem isto.
   */
  handshakeTimeoutMs?: number;
  /**
   * De quanto em quanto tempo reenviar o `hello` enquanto espera. Injetável para
   * o teste NEVER precisar esperar 1,2 s de relógio de parede.
   */
  helloRetryMs?: number;
}

export interface Connection {
  readonly welcome: Welcome;
  /** Ida e volta medida no último `pong`, em ms. `undefined` antes do primeiro. */
  readonly rttMs: number | undefined;
  /** Último `seq` que o servidor confirmou ter aplicado. Base da reconciliação. */
  readonly lastAckSeq: number;
  readonly closed: boolean;
  /**
   * `REJECT_CODES` que derrubou a conexão, quando foi o servidor que a encerrou
   * por decisão — `undefined` quando o socket caiu por rede.
   *
   * Existe porque `onClose` entrega só uma string, e string de motivo NEVER é
   * base para decidir o que a TELA mostra: "partida encerrada" e "a sala quebrou"
   * pedem palavras diferentes ao jogador, e distinguir por texto seria decidir
   * gameplay lendo mensagem de log.
   */
  readonly closeCode: number | undefined;
  onSnapshot(handler: (snapshot: Snapshot) => void): void;
  onClose(handler: (reason: string) => void): void;
  /** Devolve o `seq` usado — a predição guarda o input por `seq` para reexecutar. */
  sendIntent(intent: Intent): number;
  sendPing(): void;
  close(): void;
}

const DEFAULT_HANDSHAKE_TIMEOUT_MS = 5000;

/**
 * De quanto em quanto tempo o `hello` é REENVIADO enquanto o `welcome` não chega.
 *
 * MEDIDO no E2E: em 4 execuções da suíte cheia, a única que caiu no `rede-ruim`
 * foi a única em que o cano comeu um `hello`. Com um `hello` só, 5% de perda é
 * 5% de jogador vendo tela morta — e 5% de perda é a condição de projeto do
 * `PORTÃO 01`, e NEVER o pior caso da 4G.
 *
 * 1200 ms, e NEVER menos: o teto de ida e volta do cenário é 300 ms, e reenviar
 * antes de a resposta ter tido tempo de voltar é fabricar `hello` duplicado a
 * cada tentativa. Cabem 3 reenvios dentro dos 5 s, e três perdas seguidas em
 * cima de 5% é 1 em 8 mil.
 *
 * NEVER é reconexão: reconectar é outra fatia, e vale para o socket que MORREU.
 * Isto vale para o socket que está VIVO e mudo.
 */
const HELLO_RETRY_MS = 1200;

class ProtocolConnection implements Connection {
  private welcomeData: Welcome | undefined;
  /** Quantos `hello` saíram. Só existe para o teste poder provar que houve reenvio. */
  private helloAttempts = 0;
  private snapshotHandler: ((snapshot: Snapshot) => void) | undefined;
  private closeHandler: ((reason: string) => void) | undefined;
  private closedReason: string | undefined;
  private closedCode: number | undefined;
  private seq = 0;
  private rtt: number | undefined;
  private ack = 0;
  private settleHandshake: ((result: Welcome | Error) => void) | undefined;

  constructor(
    private readonly transport: Transport,
    private readonly now: () => number,
  ) {}

  get welcome(): Welcome {
    if (this.welcomeData === undefined) throw new Error('conexão usada antes do welcome');
    return this.welcomeData;
  }
  get rttMs(): number | undefined {
    return this.rtt;
  }
  get lastAckSeq(): number {
    return this.ack;
  }
  get closed(): boolean {
    return this.closedReason !== undefined;
  }
  get closeCode(): number | undefined {
    return this.closedCode;
  }
  get helloSent(): number {
    return this.helloAttempts;
  }

  /**
   * O handler é registrado ANTES do `hello` de propósito: com transporte em
   * memória a resposta chega dentro do próprio `send`, e registrar depois
   * perderia o `welcome`.
   */
  handshake(options: ConnectOptions): Promise<Welcome> {
    this.transport.onMessage((bytes) => {
      this.dispatch(bytes);
    });
    this.transport.onClose((reason) => {
      this.finish(reason);
    });

    const hello = encode('hello', {
      protocolVersion: PROTOCOL_VERSION,
      roomId: options.roomId,
      guestToken: options.guestToken,
    });

    const waiting = new Promise<Welcome>((resolve, reject) => {
      const timer = setTimeout(() => {
        this.settleHandshake?.(new Error('tempo esgotado esperando welcome'));
      }, options.handshakeTimeoutMs ?? DEFAULT_HANDSHAKE_TIMEOUT_MS);

      // O reenvio é um intervalo, e NEVER uma cadeia de timeouts: o que interessa
      // é insistir até o teto acima, e o teto continua sendo quem decide desistir.
      const retry = setInterval(() => {
        this.helloAttempts += 1;
        this.transport.send(hello);
      }, options.helloRetryMs ?? HELLO_RETRY_MS);

      this.settleHandshake = (result) => {
        this.settleHandshake = undefined;
        clearTimeout(timer);
        clearInterval(retry);
        if (result instanceof Error) {
          this.transport.close();
          reject(result);
          return;
        }
        resolve(result);
      };
    });

    this.helloAttempts = 1;
    this.transport.send(hello);
    return waiting;
  }

  sendIntent(intent: Intent): number {
    this.seq += 1;
    this.transport.send(
      encode('input', {
        seq: this.seq,
        tick: intent.tick,
        moveX: intent.moveX,
        moveZ: intent.moveZ,
        actions: intent.actions,
      }),
    );
    return this.seq;
  }

  sendPing(): void {
    this.transport.send(encode('ping', { t: this.stamp() }));
  }

  onSnapshot(handler: (snapshot: Snapshot) => void): void {
    this.snapshotHandler = handler;
  }

  onClose(handler: (reason: string) => void): void {
    this.closeHandler = handler;
    if (this.closedReason !== undefined) handler(this.closedReason);
  }

  close(): void {
    this.transport.close();
  }

  /** `ping.t` é u32: o relógio entra truncado e a conta volta em aritmética u32. */
  private stamp(): number {
    return Math.round(this.now()) >>> 0;
  }

  private dispatch(bytes: Uint8Array): void {
    let message: AnyMessage;
    try {
      message = decode(bytes);
    } catch (error) {
      if (error instanceof DecodeError) {
        this.fail(`pacote inválido do servidor: ${error.message}`);
        return;
      }
      throw error;
    }

    switch (message.type) {
      case 'welcome': {
        // `welcome` repetido é o servidor respondendo ao nosso REENVIO de `hello`,
        // e é esperado: os dois cruzam no cano quando a resposta demora mais que
        // `HELLO_RETRY_MS`. O primeiro é o que vale, e o segundo é descartado
        // aqui — sem isto, o assento seria reescrito por uma cópia atrasada.
        if (this.welcomeData !== undefined) return;
        const version = message.data.protocolVersion;
        if (version < MIN_SUPPORTED_VERSION || version > PROTOCOL_VERSION) {
          this.fail(
            `servidor fala protocolo ${String(version)}, cliente ${String(PROTOCOL_VERSION)}`,
          );
          return;
        }
        this.welcomeData = message.data;
        this.settleHandshake?.(message.data);
        return;
      }

      case 'snapshot':
        this.ack = message.data.ackSeq;
        this.snapshotHandler?.(message.data);
        return;

      case 'pong':
        // Diferença em u32 fecha certo mesmo quando o carimbo dá a volta.
        this.rtt = (this.stamp() - message.data.t) >>> 0;
        return;

      case 'reject': {
        const { code, message: text } = message.data;
        const error = new RejectedError(code, `servidor recusou (${String(code)}): ${text}`);
        if (this.settleHandshake !== undefined) {
          this.settleHandshake(error);
          return;
        }
        this.fail(error.message, code);
        return;
      }

      default:
        // Mensagem de cliente vindo do servidor é protocolo violado dos dois lados.
        this.fail(`mensagem inesperada do servidor: ${message.type}`);
    }
  }

  /** Erro de protocolo: derruba a conexão, NEVER continua fingindo que dá. */
  private fail(reason: string, code?: number): void {
    this.settleHandshake?.(new Error(reason));
    this.transport.close();
    this.finish(reason, code);
  }

  /**
   * `code` é gravado ANTES de `closeHandler` de propósito: quem trata o
   * fechamento lê `closeCode` de dentro do próprio handler, e ordem invertida
   * entregaria `undefined` justamente na única chamada que importa.
   */
  private finish(reason: string, code?: number): void {
    this.settleHandshake?.(new Error(`conexão fechada: ${reason}`));
    if (this.closedReason !== undefined) return;
    this.closedReason = reason;
    this.closedCode = code;
    this.closeHandler?.(reason);
  }
}

/**
 * Abre a conexão e só devolve depois do `welcome`. Quem chama recebe uma conexão
 * já pronta para jogar, ou uma exceção — NEVER um objeto em estado intermediário.
 */
export async function connect(options: ConnectOptions): Promise<Connection> {
  const transport = options.transport ?? new WebSocketTransport();
  const connection = new ProtocolConnection(transport, options.now ?? (() => performance.now()));
  await transport.connect(options.url);
  await connection.handshake(options);
  return connection;
}
